import type { PunchMapHighlight } from "./punch-map-highlight";

// Cross-route handoff for the Timeclock "Show on map" control (G3a).
//
// The Timeclock report lives on /schedule and the Command map lives on /, so the coordinates the
// operator just asked for have to survive exactly one navigation. Two things this deliberately
// is NOT:
//
//   1. NOT a URL parameter. Putting lat/lng in the address bar writes a guard's location into
//      browser history, shoulder-surfing range, and any screenshot of the console: the same
//      broad exposure the coordinate-free `management_timeclock_log` exists to avoid (G5a's
//      spirit). The operator asked to see one point on a map, not to mint a shareable link.
//   2. NOT sessionStorage or localStorage. Those persist the coordinate past the moment of use,
//      on disk, for any later reader of the profile. Module state dies with the tab.
//
// Read-once by design: takePunchHighlight() clears as it reads, so a stale coordinate cannot be
// replayed onto CAD by a later, unrelated visit to the Command Center.

let pending: PunchMapHighlight | null = null;

/** Stage a highlight for the next Command Center mount. Replaces any unread stash. */
export function stashPunchHighlight(highlight: PunchMapHighlight): void {
  pending = highlight;
}

/** Take the staged highlight and clear it. Returns null when nothing was staged. */
export function takePunchHighlight(): PunchMapHighlight | null {
  const staged = pending;
  pending = null;
  return staged;
}

/** Drop an unread stash, e.g. a navigation that never reached the map. */
export function clearPunchHighlight(): void {
  pending = null;
}

/** A taken highlight plus the workspace it was taken in. */
export interface StagedPunchHighlight {
  accountId: string;
  highlight: PunchMapHighlight;
}

/** Take the staged highlight and stamp it with the workspace that is asking for it. */
export function takePunchHighlightForAccount(accountId: string): StagedPunchHighlight | null {
  const staged = takePunchHighlight();
  return staged === null ? null : { accountId, highlight: staged };
}

/**
 * The highlight to paint right now. A workspace switch (account picker or support act-as)
 * changes the answer to null without clearing state: the Command Center does not remount on a
 * switch, and one tenant's punch must never appear on another tenant's map.
 */
export function resolvePunchHighlight(
  staged: StagedPunchHighlight | null,
  accountId: string,
): PunchMapHighlight | null {
  if (staged === null || staged.accountId !== accountId) {
    return null;
  }
  return staged.highlight;
}
